export type ResourceType = 'guide' | 'webinar' | 'template' | 'report' | 'video';
export type ResourceCategory = 'Beginner' | 'Intermediate' | 'Advanced' | 'Legal' | 'Finance' | 'Market';

export interface Resource {
  id: string;
  title: string;
  type: ResourceType;
  category: ResourceCategory;
  description: string;
  readTime?: string;
  duration?: string;
  date: string;
  featured: boolean;
  imageUrl: string;
  tags: string[];
}

export const resources: Resource[] = [
  {
    id: 'r001',
    title: 'The Complete Guide to Tax-Delinquent Property Investing',
    type: 'guide',
    category: 'Beginner',
    description: 'Master the fundamentals of finding, analyzing, and acquiring tax-delinquent properties across California, Nevada, and Arizona. Includes step-by-step checklists and real case studies.',
    readTime: '45 min read',
    date: '2026-02-15',
    featured: true,
    imageUrl: 'https://public.readdy.ai/ai/img_res/45f814b8256c8fc340e4db42a164fdc3.jpg',
    tags: ['Tax Liens', 'Beginner', 'California', 'Strategy'],
  },
  {
    id: 'r002',
    title: 'Foreclosure Auction Bidding Strategies That Win',
    type: 'guide',
    category: 'Advanced',
    description: 'Advanced tactics used by top members to consistently outbid competition while protecting maximum profit margins. Includes psychological bidding frameworks and real auction transcripts.',
    readTime: '32 min read',
    date: '2026-01-20',
    featured: true,
    imageUrl: 'https://readdy.ai/api/search-image?query=courthouse%20steps%20auction%20gavel%20judge%20hammer%20real%20estate%20foreclosure%20bidding%20professional%20documentation%20strategy%20planning%20business&width=600&height=380&seq=res002&orientation=landscape',
    tags: ['Auctions', 'Advanced', 'Bidding', 'Strategy'],
  },
  {
    id: 'r003',
    title: 'January 2026 Southwest Market Report',
    type: 'report',
    category: 'Market',
    description: 'Comprehensive analysis of foreclosure trends, tax-delinquent property volumes, and auction clearance rates across CA, NV, and AZ markets. Updated monthly with exclusive data.',
    readTime: '25 min read',
    date: '2026-01-30',
    featured: true,
    imageUrl: 'https://readdy.ai/api/search-image?query=market%20analysis%20report%20data%20charts%20graphs%20real%20estate%20trends%20bar%20charts%20statistics%20professional%20business%20analytical%20dashboard%20warm%20neutral&width=600&height=380&seq=res003&orientation=landscape',
    tags: ['Market Report', 'California', 'Nevada', 'Arizona', 'Data'],
  },
  {
    id: 'r004',
    title: 'California Foreclosure Law: What Every Investor Must Know',
    type: 'guide',
    category: 'Legal',
    description: 'Navigate California\'s complex non-judicial foreclosure process, redemption rights, and title transfer procedures. Updated for 2026 legislative changes.',
    readTime: '38 min read',
    date: '2025-12-10',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=legal%20documents%20law%20books%20California%20real%20estate%20attorney%20desk%20professional%20gavel%20paperwork%20warm%20office%20neutral%20background&width=600&height=380&seq=res004&orientation=landscape',
    tags: ['Legal', 'California', 'Foreclosure Law', '2026'],
  },
  {
    id: 'r005',
    title: 'Live Webinar: Identifying Hidden Equity in AZ Properties',
    type: 'webinar',
    category: 'Advanced',
    description: 'Expert panel webinar featuring three veteran Arizona investors sharing their proprietary methods for identifying undervalued foreclosure properties with 50%+ equity positions.',
    duration: '1h 45m',
    date: '2026-03-15',
    featured: true,
    imageUrl: 'https://readdy.ai/api/search-image?query=webinar%20online%20meeting%20real%20estate%20professional%20virtual%20presentation%20experts%20panel%20screen%20discussion%20investment%20strategy%20modern%20office&width=600&height=380&seq=res005&orientation=landscape',
    tags: ['Webinar', 'Arizona', 'Equity Analysis', 'Expert Panel'],
  },
  {
    id: 'r006',
    title: 'Due Diligence Checklist for Foreclosure Properties',
    type: 'template',
    category: 'Beginner',
    description: 'Our 47-point due diligence checklist ensures you never miss a critical step before bidding on any foreclosure property. Downloadable PDF with fillable fields.',
    readTime: '15 min read',
    date: '2025-11-05',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=checklist%20clipboard%20due%20diligence%20property%20inspection%20document%20real%20estate%20investment%20professional%20form%20pen%20desk%20neutral%20background&width=600&height=380&seq=res006&orientation=landscape',
    tags: ['Checklist', 'Template', 'Due Diligence', 'All States'],
  },
  {
    id: 'r007',
    title: 'Nevada Tax Lien Certificate Investment Guide',
    type: 'guide',
    category: 'Intermediate',
    description: 'A comprehensive guide to purchasing tax lien certificates in Nevada counties including Clark, Washoe, and Carson City. Includes historical yield data and redemption rate analysis.',
    readTime: '42 min read',
    date: '2025-10-28',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=Nevada%20state%20map%20certificate%20documents%20investment%20finance%20tax%20lien%20professional%20business%20office%20desk%20warm%20lighting%20strategy&width=600&height=380&seq=res007&orientation=landscape',
    tags: ['Nevada', 'Tax Liens', 'Certificates', 'Finance'],
  },
  {
    id: 'r008',
    title: 'Foreclosure Purchase Agreement Template Package',
    type: 'template',
    category: 'Legal',
    description: 'Attorney-drafted purchase agreement templates for all three states (CA, NV, AZ) plus addenda for foreclosure-specific contingencies, title issues, and as-is conditions.',
    readTime: '20 min read',
    date: '2025-12-20',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=legal%20contract%20purchase%20agreement%20template%20real%20estate%20documents%20professional%20attorney%20reviewed%20paper%20forms%20signature%20lines%20neutral%20desk&width=600&height=380&seq=res008&orientation=landscape',
    tags: ['Templates', 'Legal', 'Purchase Agreement', 'All States'],
  },
  {
    id: 'r009',
    title: 'Webinar: Financing Your Foreclosure Acquisitions',
    type: 'webinar',
    category: 'Finance',
    description: 'Discover creative financing strategies for foreclosure properties including hard money lending, private capital, self-directed IRAs, and bridge loan optimization.',
    duration: '2h 10m',
    date: '2026-02-20',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=financing%20strategies%20webinar%20presentation%20financial%20chart%20money%20real%20estate%20investment%20creative%20funding%20professional%20speaker%20presentation%20screen&width=600&height=380&seq=res009&orientation=landscape',
    tags: ['Finance', 'Funding', 'Hard Money', 'Creative Financing'],
  },
  {
    id: 'r010',
    title: 'ROI Calculator & Property Analysis Spreadsheet',
    type: 'template',
    category: 'Finance',
    description: 'Advanced Excel/Google Sheets template with built-in ROI projections, rehab cost estimator, rental income calculator, and exit strategy comparison tool.',
    readTime: '10 min setup',
    date: '2026-01-10',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=spreadsheet%20calculator%20financial%20analysis%20real%20estate%20ROI%20data%20charts%20Excel%20Google%20Sheets%20investment%20property%20analysis%20professional%20desk%20laptop&width=600&height=380&seq=res010&orientation=landscape',
    tags: ['Calculator', 'Template', 'ROI', 'Analysis'],
  },
  {
    id: 'r011',
    title: 'Arizona Q1 2026: Top 10 Emerging Foreclosure Markets',
    type: 'report',
    category: 'Market',
    description: 'Data-driven analysis identifying the top 10 Arizona zip codes with highest foreclosure activity, fastest appreciation rates, and strongest rental demand entering Q2 2026.',
    readTime: '28 min read',
    date: '2026-03-01',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=Arizona%20market%20analysis%20real%20estate%20growth%20data%20emerging%20neighborhoods%20map%20statistics%20professional%20report%20business%20analytics%20warm%20tones&width=600&height=380&seq=res011&orientation=landscape',
    tags: ['Arizona', 'Market Report', 'Q1 2026', 'Emerging Markets'],
  },
  {
    id: 'r012',
    title: 'Video: Walkthrough of a Successful Tax Sale Redemption',
    type: 'video',
    category: 'Advanced',
    description: 'A real member shares their complete experience recovering a $780K property through the tax sale redemption process in Sacramento County, from discovery to closing.',
    duration: '52 min',
    date: '2026-02-28',
    featured: false,
    imageUrl: 'https://readdy.ai/api/search-image?query=video%20tutorial%20real%20estate%20investment%20walkthrough%20house%20property%20acquisition%20success%20story%20professional%20filming%20documentary&width=600&height=380&seq=res012&orientation=landscape',
    tags: ['Video', 'Case Study', 'California', 'Tax Sale'],
  },
];

export const membershipPlans = [
  {
    id: 'basic',
    name: 'Starter',
    price: 97,
    period: 'month',
    description: 'Perfect for investors just getting started with foreclosure properties.',
    color: '#e8c84a',
    features: [
      'Access to property listings (50/month)',
      'Basic market reports (quarterly)',
      'Starter investment guides library',
      'Email property alerts (weekly)',
      'Community forum access',
      'Basic due diligence templates',
    ],
    notIncluded: [
      'AI-powered analytics',
      'Pre-market property alerts',
      'Legal template package',
      'Expert legal consultations',
      'Priority auction support',
    ],
  },
  {
    id: 'pro',
    name: 'Professional',
    price: 247,
    period: 'month',
    description: 'Our most popular plan for serious investors building their portfolio.',
    color: '#c9a227',
    popular: true,
    features: [
      'Unlimited property listings access',
      'Monthly market reports (all 3 states)',
      'Full guides & webinar library',
      'Daily property alerts (30-60 days early)',
      'AI-powered ROI analytics',
      'Complete legal template package',
      'Private investor community',
      'Monthly expert Q&A webinar',
      '2 legal consultations per year',
    ],
    notIncluded: [
      'Priority auction support',
      'Dedicated acquisition specialist',
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 497,
    period: 'month',
    description: 'Full-service support for high-volume investors and syndicators.',
    color: '#1a2332',
    features: [
      'Everything in Professional',
      'Pre-market alerts (60-90 days early)',
      'Priority auction support & guidance',
      'Dedicated acquisition specialist',
      'Unlimited legal consultations',
      'Custom property sourcing requests',
      'Direct line to legal team',
      'Exclusive Elite-only deal flow',
      'Portfolio review & strategy sessions',
      'Partnership deal opportunities',
    ],
    notIncluded: [],
  },
];
